import { useEffect, useCallback, useState, useRef } from 'react';
import { Button } from '../ui/button';
import { X, Download, ExternalLink, ZoomIn, ZoomOut, RotateCcw, SplitSquareHorizontal } from 'lucide-react';

interface ImageViewerModalProps {
  isOpen: boolean;
  imageUrl: string;
  filename: string;
  onClose: () => void;
  onDownload?: () => void;
  /** Original URL for comparison - if provided and different from imageUrl, shows Compare button */
  originalUrl?: string;
  /** Called when Compare button is clicked */
  onCompare?: () => void;
}

export function ImageViewerModal({
  isOpen,
  imageUrl,
  filename,
  onClose,
  onDownload,
  originalUrl,
  onCompare,
}: ImageViewerModalProps) {
  // Can compare if we have originalUrl, it's different from imageUrl (meaning image is processed), and onCompare handler
  const canCompare = originalUrl && originalUrl !== imageUrl && onCompare;
  const [zoom, setZoom] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use refs for drag state to avoid re-renders
  const isDraggingRef = useRef(false);
  const positionRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });

  const updateImageTransform = useCallback(() => {
    if (imageRef.current) {
      const { x, y } = positionRef.current;
      imageRef.current.style.transform = `scale(${zoom}) translate(${x / zoom}px, ${y / zoom}px)`;
    }
  }, [zoom]);

  // Handle ESC key to close
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      // Reset state when opening
      setZoom(1);
      positionRef.current = { x: 0, y: 0 };
      setIsLoading(true);
      if (imageRef.current) {
        imageRef.current.style.transform = 'scale(1) translate(0px, 0px)';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Update transform when zoom changes
  useEffect(() => {
    updateImageTransform();
  }, [zoom, updateImageTransform]);

  if (!isOpen) return null;

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - 0.5, 1);
    if (newZoom === 1) {
      positionRef.current = { x: 0, y: 0 };
    }
    setZoom(newZoom);
  };

  const handleReset = () => {
    positionRef.current = { x: 0, y: 0 };
    setZoom(1);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isDraggingRef.current) {
      onClose();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      e.preventDefault();
      isDraggingRef.current = true;
      dragStartRef.current = {
        x: e.clientX - positionRef.current.x,
        y: e.clientY - positionRef.current.y,
      };
      if (imageRef.current) {
        imageRef.current.style.cursor = 'grabbing';
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDraggingRef.current && zoom > 1) {
      positionRef.current = {
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      };
      // Direct DOM manipulation for smooth performance
      if (imageRef.current) {
        const { x, y } = positionRef.current;
        imageRef.current.style.transform = `scale(${zoom}) translate(${x / zoom}px, ${y / zoom}px)`;
      }
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    if (imageRef.current && zoom > 1) {
      imageRef.current.style.cursor = 'grab';
    }
  };

  // Double click to zoom
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (zoom === 1) {
      setZoom(2);
    } else {
      positionRef.current = { x: 0, y: 0 };
      setZoom(1);
    }
  };

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.25 : 0.25;
    const newZoom = Math.min(Math.max(zoom + delta, 1), 4);
    if (newZoom === 1) {
      positionRef.current = { x: 0, y: 0 };
    }
    setZoom(newZoom);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95"
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
        <h3 className="text-white font-medium truncate max-w-md">
          {filename}
        </h3>

        <div className="flex items-center gap-2">
          {/* Zoom Controls */}
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1 mr-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomOut}
              disabled={zoom <= 1}
              className="h-8 w-8 text-white hover:bg-white/20 disabled:opacity-50"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-white text-sm px-2 min-w-[60px] text-center font-mono">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomIn}
              disabled={zoom >= 4}
              className="h-8 w-8 text-white hover:bg-white/20 disabled:opacity-50"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            {zoom > 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleReset}
                className="h-8 w-8 text-white hover:bg-white/20 ml-1"
                title="Reset zoom"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Compare button - only shown for processed images */}
          {canCompare && (
            <Button
              variant="ghost"
              onClick={onCompare}
              className="h-10 px-4 text-white hover:bg-blue-500/80 bg-blue-500/50 gap-2"
            >
              <SplitSquareHorizontal className="h-5 w-5" />
              Compare
            </Button>
          )}

          {onDownload && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDownload}
              className="h-10 w-10 text-white hover:bg-white/20"
            >
              <Download className="h-5 w-5" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.open(imageUrl, '_blank')}
            className="h-10 w-10 text-white hover:bg-white/20"
          >
            <ExternalLink className="h-5 w-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10 text-white hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Image Container */}
      <div
        ref={containerRef}
        className="flex items-center justify-center w-full h-full p-16 overflow-hidden"
        onWheel={handleWheel}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        )}
        <img
          ref={imageRef}
          src={imageUrl}
          alt={filename}
          className="max-w-full max-h-full object-contain select-none"
          style={{
            cursor: zoom > 1 ? 'grab' : 'zoom-in',
            willChange: 'transform',
            transform: 'scale(1) translate(0px, 0px)',
          }}
          onLoad={() => setIsLoading(false)}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          draggable={false}
        />
      </div>

      {/* Footer hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs flex items-center gap-3">
        <span>ESC to close</span>
        <span>Scroll to zoom</span>
        <span>Double-click to toggle</span>
        {zoom > 1 && <span>Drag to pan</span>}
      </div>
    </div>
  );
}
